/** How a `ref_*` cell value is rendered in the Records grid, and whether the
 *  CSS ellipsis will be hiding part of it.
 *
 *  Truncation is decided by string length, not by measuring the DOM
 *  (`scrollWidth > clientWidth`), for three reasons: a page is up to 50 rows ×
 *  ~15 columns, so per-cell measurement plus a ResizeObserver would mean
 *  hundreds of forced reflows per render; jsdom reports `scrollWidth: 0`, which
 *  makes overflow detection untestable; and the cell is a fixed
 *  `max-w-[320px] mono text-[12px]` box, so the character budget is stable
 *  (~42 chars at a ~7.2px mono advance). A value just under the limit in an
 *  unusually narrow column gets no balloon — it is still readable in the
 *  row-edit dialog.
 */
export const CELL_TRUNCATE_AT = 40;

/** Display text of a cell, plus whether it overflows the column.
 *
 *  `text` is always the **whole** value: the ellipsis is applied by CSS, and the
 *  balloon exists precisely to show what the ellipsis hides. An absent value
 *  renders as an em dash and is never reported as truncated. */
export function cellDisplay(
  value: unknown,
  limit: number = CELL_TRUNCATE_AT,
): { text: string; truncated: boolean } {
  if (value === null || value === undefined) return { text: "—", truncated: false };
  const text =
    typeof value === "boolean"
      ? value
        ? "true"
        : "false"
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
  return { text, truncated: text.length > limit };
}

/** A cell value as an `<input>`/`<textarea>` string. Objects (jsonb) are shown
 *  as JSON text and sent back verbatim — the backend decodes/coerces them. */
export function toInputString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
